import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ThemeEditor, useThemeEditor } from '@appcues/theme-editor';
import { Button } from '@appcues/sonar';
import { Label, FieldSet, Select } from 'ext/components/ui';
import startCase from 'lodash.startcase';
import { selectLocales, Shape as LocalesShape } from 'ext/entities/locales';
import { resolve as resolveExperienceTheme } from 'entities/compiled-theme/actions';
import { selectCurrentTheme } from 'entities/compiled-theme/reducer';
import CompiledThemeShape from 'entities/compiled-theme/shape';
import { EditorShape } from 'entities/step-groups/shape';
import { BlockContentShape } from 'entities/block/shape';
import {
  eligibleThemeVariants,
  updateToThemed,
  EDITOR_MAPPING,
} from 'lib/themes';
import { LAYOUT_TRAITS } from 'lib/trait';
import { selectUserPreferences } from 'entities/user-preferences';
import { DEFAULT_LOCALE } from 'lib/localization';
import { ThemeOverridesPanel } from './ThemeOverridesPanel';
import { Controls } from './styled';

const ThemeVariantSelect = ({
  type,
  editor,
  content,
  contentWithVariations,
  compiledTheme,
  isDefaultLocale,
  locales,
  onChange,
  onThemePublished,
}) => {
  const { mobileThemes } = useFlags();

  const { setIsEditorOpen } = useThemeEditor();

  const isTraitMode = LAYOUT_TRAITS.includes(type);

  const currentTheme = isTraitMode
    ? editor?.style?.theme
    : content?.style?.theme;

  const eligibleVariants = eligibleThemeVariants(type, compiledTheme)
    .map(key => ({
      value: key,
      label: startCase(key.split('.').pop()),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const updateToVariant = selectedVariant => {
    if (isTraitMode) {
      onChange(
        updateToThemed({
          type: EDITOR_MAPPING,
          content: editor,
          selectedVariant,
        })
      );
    } else {
      onChange({
        blockId: contentWithVariations.id,
        contentChunk: updateToThemed({
          type,
          content: contentWithVariations,
          selectedVariant,
        }),
        useOriginalId: true,
        replace: true,
      });
    }
  };

  const handleSelectChange = selected => {
    updateToVariant(selected.value);
  };

  const handleResetProperty = updatedChunk => {
    if (isTraitMode) {
      onChange(updatedChunk);
    } else {
      onChange({
        blockId: contentWithVariations.id,
        contentChunk: updatedChunk,
        useOriginalId: true,
        replace: true,
      });
    }
  };

  const handleThemePublished = (updatedCompiledTheme, metadata) => {
    onThemePublished(updatedCompiledTheme);

    if (metadata.updateFromDonation) {
      // If adding as a new variant, update the variant to the new one,
      // or if updating an existing variant, reset so there's no longer any overrides
      updateToVariant(metadata.updateFromDonation);
    }
  };

  if (!mobileThemes || !isDefaultLocale || eligibleVariants.length === 0) {
    return null;
  }

  return (
    <Controls flex>
      <FieldSet>
        <Label htmlFor="theme-variant">Theme variant</Label>
        <Select
          id="theme-variant"
          aria-label="Theme variant"
          placeholder="Select a theme variant"
          options={eligibleVariants}
          value={eligibleVariants.filter(({ value }) => currentTheme === value)}
          onChange={handleSelectChange}
        />
      </FieldSet>
      <Button fluid variant="secondary" onClick={() => setIsEditorOpen(true)}>
        Edit theme
      </Button>
      {currentTheme && (
        <ThemeOverridesPanel
          compiledTheme={compiledTheme}
          type={type}
          editor={editor}
          content={content}
          contentWithVariations={contentWithVariations}
          locales={locales}
          onResetAll={() => updateToVariant(currentTheme)}
          onResetProperty={handleResetProperty}
        />
      )}
      <ThemeEditor
        platform="mobile"
        id={compiledTheme.id}
        onPublish={handleThemePublished}
      />
    </Controls>
  );
};

ThemeVariantSelect.propTypes = {
  type: PropTypes.string,
  editor: EditorShape,
  content: BlockContentShape,
  contentWithVariations: BlockContentShape,
  compiledTheme: CompiledThemeShape,
  isDefaultLocale: PropTypes.bool,
  locales: PropTypes.objectOf(LocalesShape),
  onChange: PropTypes.func,
  onThemePublished: PropTypes.func,
};

const mapStateToProps = state => ({
  compiledTheme: selectCurrentTheme(state),
  isDefaultLocale: selectUserPreferences(state).locale.id === DEFAULT_LOCALE.id,
  locales: selectLocales(state) ?? {},
});

const mapDispatchToProps = {
  onThemePublished: resolveExperienceTheme,
};

export default connect(mapStateToProps, mapDispatchToProps)(ThemeVariantSelect);
